import { postSectionOfLesson } from 'core/crud/lessons.crud';
import useAmplitude from 'core/hooks/useAmplitude';
import { useCourse } from 'core/lite/hooks/useCourse';
import { parseEducationYearName } from 'core/lite/parsers';
import ErrorText from 'core/modules/atoms/ErrorText';
import PublishScoreSection from 'core/modules/components/forms/PublishScoreSection/PublishScoreSection';
import FooterButtons from 'core/modules/compositions/FooterButtons';
import TextEditor from 'core/modules/compositions/wysiwyg/TextEditor';
import { hasDueDate } from 'core/utils/assessmentDeadline';
import { extraConfigNoTabTrap } from 'core/utils/froalaConfig';
import { LEMONADE_QUESTION_TYPES_VANILLA } from 'core/utils/lemonade';
import { parseUsers } from 'core/utils/parses';
import { hasDescriptionChanged, toEditorModel, toPersistedDescription } from 'core/utils/richDescription';
import { AMPLITUDE_EVENT, CONTENT_TYPES } from 'core/variables/constant';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { DatePickerActions, InputRadio, SelectCustomizable } from 'ui';
import InputText from 'ui/atoms/InputText/InputText';
// `getFormFieldLabelId` no forma parte del barrel `ui`: se importa de su módulo. Exponerlo
// en el barrel no funciona aquí, porque este módulo entra en el ciclo
// `ui/index.ts -> ... -> core -> 'ui'` que lo deja a medio evaluar (comprobado: en este
// contexto también salen undefined `Button` y `TextHtml`).
import { getFormFieldLabelId } from 'ui/hooks/useFormFieldAria';
import FormField from 'ui/molecules/FormField/FormField';
// Ruta directa (no el barrel 'ui/shared'): ese barrel importa DialogNewTest, que a su vez importa este mismo
// formulario, así que hacerlo por barrel cerraría el ciclo de evaluación que documenta .claude/rules/components.md.
import { createLateDeliveryCheckboxRenderer } from 'ui/shared/forms/LateDeliveryCheckbox/LateDeliveryCheckbox';
import StudentsSelect from '../../selects/StudentsSelect';

// Id explícito: FormField lo clona en el TextEditor y de él se deriva el id del <label>,
// al que el contenteditable de Froala apunta con aria-labelledby.
const DESCRIPTION_FIELD_ID = 'new-test-form-description';

/**
 * Indica si la descripción debe viajar en el objeto que emite el formulario. La saga
 * `updateTest` hace su `putSectionOfLesson` SIN `course_guid`, así que lo que viaje aquí se
 * escribe en el lesson item ORIGINAL y se propaga a todos los cursos que lo referencian (el
 * mismo motivo por el que TAN-9535 sacó de ese PUT status y published_at). Como Froala
 * normaliza el texto plano heredado al montar y emite su onChange sin que nadie toque el
 * campo, al editar solo se emite si cambió de verdad: omitirla hace que el
 * `{ ...test, ...newTest }` del header conserve la que ya estaba guardada.
 * @param {string} initialDescription - Descripción con la que se abrió el diálogo
 * @param {string} currentDescription - Descripción que tiene el formulario
 * @param {boolean} isEditingTest - Si el diálogo edita un quiz existente
 * @returns {boolean} True si hay que emitirla
 */
function shouldSendDescription(initialDescription, currentDescription, isEditingTest) {
  return !isEditingTest || hasDescriptionChanged(initialDescription, currentDescription);
}

const NewTestForm = ({
  t,
  onCancel,
  onSuccess,
  currentStudents,
  currentTest,
  isEditing,
  sections,
  students,
  onSectionCreated,
  isPublisherMode,
  preSelectionType,
  setSelectedTemplate,
  buttonTextSuccess,
  isAutoevaluative,
  shouldShowPublishScore,
}) => {
  const EMPTY_FORM_STATE = {
    name: '',
    description: '',
    can_deliver_late: false,
    ...(preSelectionType === CONTENT_TYPES.QUESTION && { selectedTemplate: LEMONADE_QUESTION_TYPES_VANILLA.LONG_TEXT }),
    dueDate: null,
    publish_score_at: null,
    autoPublishScore: false,
    sameAsDueDate: false,
    duration: '',
    taskType: 'evaluative',
    type_guid: preSelectionType,
    section: sections.length
      ? sections[sections.length - 1]
      : {
          id: '',
          label: '',
        },
  };
  const { sendAmplitudeEvent } = useAmplitude();
  const { addToast } = useToasts();
  const { lessonGuid, courseGuid } = useParams();
  const [errors, setErrors] = useState({});
  const [canSubmit, setCanSubmit] = useState(false);
  const [test, setTest] = useState(isEditing ? { ...currentTest, autoPublishScore: !!currentTest?.publish_score_at, sameAsDueDate: false } : EMPTY_FORM_STATE);
  const [selectedStudents, setSelectedStudents] = useState(isEditing && currentStudents ? parseUsers(currentStudents) : parseUsers(students));
  const [listOfSections, setListOfSections] = useState(sections);
  const [selectedSection, setSelectedSection] = useState(settingInitialSection(currentTest));
  const [hasChangedDueDate, setHasChangedDueDate] = useState(false);
  // Las descripciones heredadas son texto plano del antiguo TextArea: se convierten para
  // que el editor conserve sus saltos de línea.
  const descriptionModel = toEditorModel(test.description);

  function onChangeDescription(value) {
    setTest((prev) => ({ ...prev, description: value }));
  }

  const { data: course } = useCourse(courseGuid);

  const [isDirty, setIsDirty] = useState({
    name: false,
    description: false,
    students: false,
  });

  const listOfLemonadeTypes = [
    {
      id: LEMONADE_QUESTION_TYPES_VANILLA.LONG_TEXT,
      label: t('lemonade:Text'),
    },
    {
      id: LEMONADE_QUESTION_TYPES_VANILLA.UPLOAD_FILE,
      label: t('lemonade:Upload file'),
    },
    {
      id: LEMONADE_QUESTION_TYPES_VANILLA.AUDIO,
      label: t('lemonade:Audio'),
    },
    // {
    //   id: LEMONADE_QUESTION_TYPES_VANILLA.VIDEO,
    //   label: (
    //     <div className="new-lemonade-form__select-type">
    //       <img src={'/assets/lemonade-templates/manual-upload-file.png'} /> {t('lemonade:Video')}
    //     </div>
    //   ),
    // },
  ];

  function settingInitialSection(initalTest) {
    if (initalTest.section) return initalTest.section;
    else if (sections.length) {
      return sections[sections.length - 1];
    }
    return null;
  }

  function checkCanSubmit() {
    return !test.description || test.description?.length < 60000;
  }

  const onSubmit = (e) => {
    if (canSubmit) {
      if (checkCanSubmit()) {
        const testToEmit = {
          ...test,
          publish_score_at: test.autoPublishScore ? test.publish_score_at : null,
          section: selectedSection,
          students: selectedStudents,
        };

        if (shouldSendDescription(currentTest?.description ?? '', test.description ?? '', isEditing)) {
          testToEmit.description = toPersistedDescription(test.description);
        } else {
          delete testToEmit.description;
        }

        onSuccess(testToEmit);
        if (hasChangedDueDate) {
          sendAmplitudeEvent(
            AMPLITUDE_EVENT.SET_DELIVER_DATE,
            {
              discipline: course.discipline_name,
              content_stage: course.education_level_name,
              content_grade: parseEducationYearName(course.education_year_name),
            },
            course
          );
          setHasChangedDueDate(false);
        }
        clearFields();
        onCancel();
      } else {
        addToast(t('assigments:The description is too long'), {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    }
    e.preventDefault();
    e.stopPropagation();
  };

  function isDurationValidate(test) {
    return (test.duration && (test.duration > 480 || test.duration.length < 1)) || test.duration === 0;
  }

  let validate = useCallback(
    debounce((test, selectedStudents, isDirty) => {
      let errorsTemp = {};
      if (isDirty.name && (test.name.length > 250 || test.name.length < 3)) {
        errorsTemp = { ...errorsTemp, name: true };
      }

      if (isDirty.duration && isDurationValidate(test)) {
        errorsTemp = { ...errorsTemp, duration: true };
      }

      if (selectedSection?.id !== undefined && selectedSection.id.length === 0 && isDirty.section) {
        errorsTemp = { ...errorsTemp, section: true };
      }

      setErrors(errorsTemp);
    }, 1000),
    []
  );

  useEffect(() => {
    let canSubmitTemp = true;

    if (test.name.length > 250 || test.name.length < 3) {
      canSubmitTemp = false;
    }

    if (isDurationValidate(test)) {
      canSubmitTemp = false;
    }

    if (!selectedSection || selectedSection.id === undefined || selectedSection.id.length === 0) {
      canSubmitTemp = false;
    }

    if (test.selectedTemplate) setSelectedTemplate(test.selectedTemplate);
    setCanSubmit(canSubmitTemp);
    validate(test, selectedStudents, isDirty);
  }, [test, selectedStudents, selectedSection]);

  const onClickCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearFields();
    onCancel();
  };

  const clearFields = () => {
    setTest(EMPTY_FORM_STATE);
  };

  const onCreateSection = async (value) => {
    const newSection = await postSectionOfLesson({ lesson_guid: lessonGuid, section: value });

    if (newSection.status === 200) {
      const data = newSection.data.data;
      if (onSectionCreated) onSectionCreated(data);
      setListOfSections([...sections, { id: data.guid, label: data.section }]);
      setSelectedSection({ id: data.guid, label: data.section });
    }
  };

  function getErrorNameMessage() {
    if (errors?.name) return t('common:Required parameter');
    if (errors?.maxLength) return t('common:Max x character', { count: 70 });
    return undefined;
  }
  const nameErrorMessage = getErrorNameMessage();

  // Saving from the date picker commits both the due date and the deliver-late flag into `test`; the picker
  // owns the draft, so `test` only updates on its Save (not on every edit).
  function onSaveDueDate({ value, extraActions }) {
    setHasChangedDueDate(true);
    // Si la publicación automática oculta el check de "entregar tarde", el slot no envía valor: conserva el actual.
    // Al guardar sin fecha de entrega se persiste el flag apagado y se limpia la publicación (que requiere entrega).
    setTest({
      ...test,
      dueDate: value,
      can_deliver_late: value ? (extraActions ?? test.can_deliver_late) : false,
      ...(value ? {} : { autoPublishScore: false, publish_score_at: null, sameAsDueDate: false }),
    });
  }

  // Al vaciar la fecha de entrega se limpia la publicación (requiere entrega) y se desmarca el check.
  function onClearDueDate() {
    setHasChangedDueDate(true);
    setTest({ ...test, dueDate: null, can_deliver_late: false, autoPublishScore: false, publish_score_at: null, sameAsDueDate: false });
  }

  // El valor persistido solo puede sembrar el check si hay fecha de entrega: un `can_deliver_late` a 1 sin
  // fecha (dato antiguo, o el zero date de MySQL, que es truthy) encendería el check al elegir día sin que el
  // profesor lo tocara, y se volvería a guardar. Dentro del popover manda `draftDate`: sin fecha en edición no
  // hay plazo que ampliar, así que el check sale deshabilitado y apagado (lo resuelve LateDeliveryCheckbox).
  const hasStoredDeliverLate = hasDueDate(test.dueDate) && Boolean(test.can_deliver_late);

  // Al fijar la publicación automática, "Entregar más tarde" queda deshabilitado (se oculta del datepicker).
  const hasPublishScoreBeforeDueDate =
    test.autoPublishScore && test.publish_score_at && test.dueDate && new Date(test.publish_score_at) < new Date(test.dueDate);

  // La publicación automática solo aplica a quizzes; si no se especifica visibilidad, se infiere por el tipo.
  const isPublishScoreVisible = shouldShowPublishScore ?? preSelectionType === CONTENT_TYPES.TEST;
  // El campo se deshabilita si no es autoevaluativo o si no hay fecha de entrega (la publicación requiere entrega).
  const shouldDisablePublishScore = !isAutoevaluative || !test.dueDate;

  function onToggleAutoPublishScore() {
    if (test.autoPublishScore) {
      setTest({ ...test, autoPublishScore: false, publish_score_at: null, sameAsDueDate: false });
    } else {
      setTest({ ...test, autoPublishScore: true, can_deliver_late: false });
    }
  }

  function onChangePublishScoreAt(date) {
    setTest({ ...test, publish_score_at: date, sameAsDueDate: false });
  }

  function onClearPublishScoreAt() {
    setTest({ ...test, publish_score_at: null, sameAsDueDate: false });
  }

  function onToggleSameAsDueDate() {
    if (test.sameAsDueDate) {
      setTest({ ...test, sameAsDueDate: false });
    } else {
      setTest({ ...test, sameAsDueDate: true, publish_score_at: test.dueDate });
    }
  }

  return (
    <div className="new-test-form">
      <form onSubmit={onSubmit}>
        <div className="new-test-form__form">
          <div className={`${preSelectionType !== CONTENT_TYPES.QUESTION ? 'new-test-form__big' : 'new-test-form__six-parts'} `}>
            <FormField
              error={!!nameErrorMessage}
              errorMessage={nameErrorMessage}
              label={t('common:Title')}
            >
              <InputText
                placeholder={t('placeholder:Write here')}
                name="title"
                value={test.name}
                onChange={({ target: { value } }) => {
                  setTest({ ...test, name: value });
                  setIsDirty({ ...isDirty, name: true });
                }}
              />
            </FormField>
          </div>
          {preSelectionType === CONTENT_TYPES.QUESTION && (
            <div className="new-test-form__six-parts">
              <SelectCustomizable
                defaultValue={listOfLemonadeTypes.find((item) => item.id === test?.selectedTemplate)}
                options={listOfLemonadeTypes}
                label={t('assigments:Question type')}
                onChange={(e) => setTest({ ...test, selectedTemplate: e.id })}
                isDisabled={isEditing}
              />
            </div>
          )}
          <div
            className={`new-test-form__six-parts new-test-form__big--task-type-radio ${
              test.taskType === 'evaluative' && 'new-test-form__big--task-type-radio--selected'
            }`}
          >
            <InputRadio
              key={'evaluative'}
              name="task-type"
              label={preSelectionType === CONTENT_TYPES.TEST ? t('assigments:Evaluative quizz') : t('assigments:Evaluative')}
              description={t('assigments:Has no help')}
              checked={test.taskType === 'evaluative'}
              onChange={() => setTest({ ...test, taskType: 'evaluative' })}
            />
          </div>
          <div
            className={`new-test-form__six-parts new-test-form__big--task-type-radio ${
              test.taskType === 'formative' && 'new-test-form__big--task-type-radio--selected'
            }`}
          >
            <InputRadio
              key={'formative'}
              name="task-type"
              description={t('assigments:Has help')}
              label={preSelectionType === CONTENT_TYPES.TEST ? t('assigments:Formative quizz') : t('assigments:Formative')}
              checked={test.taskType === 'formative'}
              onChange={() => setTest({ ...test, taskType: 'formative' })}
            />
          </div>
          <div className="new-test-form__big">
            <FormField id={DESCRIPTION_FIELD_ID} label={t('common:Description-optional')}>
              <TextEditor
                ariaLabelledBy={getFormFieldLabelId(DESCRIPTION_FIELD_ID)}
                extraConfig={extraConfigNoTabTrap}
                model={descriptionModel}
                onModelChange={onChangeDescription}
                placeholder={t('placeholder:Write here')}
              />
            </FormField>
          </div>
          <div className="new-test-form__six-parts section-select__wrapper">
            <SelectCustomizable
              options={listOfSections}
              name="section"
              label={t('Section')}
              placeholder={t('placeholder:Select')}
              onChange={(e) => {
                setSelectedSection(e);
                setIsDirty({ ...isDirty, section: true });
              }}
              noOptionsMessage={listOfSections.length ? t('placeholder:No options') : t('placeholder:Start typing to create a new section')}
              value={selectedSection}
              error={errors.section && <ErrorText text={t('common:Required parameter')} />}
              canCreate={true}
              onCreateOption={onCreateSection}
              isMulti={false}
            />
          </div>
          {!isPublisherMode && (
            <div className="new-test-form__six-parts new-test-form__six-parts--students">
              <StudentsSelect
                value={selectedStudents}
                onChange={(e) => {
                  setSelectedStudents(e);
                }}
                label={t('common:To_user')}
                options={parseUsers(students)}
                labelKey={'fullname'}
                valueKey="person_guid"
              />
            </div>
          )}
          {!isPublisherMode && (
            <div className="new-test-form__six-parts">
              <div className="new-test-form__input-date-picker">
                <DatePickerActions
                  extraActions={
                    test.autoPublishScore
                      ? undefined
                      : { initial: hasStoredDeliverLate, render: createLateDeliveryCheckboxRenderer(t('assigments:Allow late delivery')) }
                  }
                  label={t('assigments:Due date')}
                  onClear={onClearDueDate}
                  onSave={onSaveDueDate}
                  shouldDisablePast
                  showHour
                  value={test.dueDate}
                />
              </div>
            </div>
          )}
          {preSelectionType === CONTENT_TYPES.TEST && (
            <div className="new-test-form__six-parts">
              <FormField
                error={!!errors.duration}
                errorMessage={t('Must be unlimited or greater than 0 and less or equal than to 480')}
                label={t('assigments:Duration in minutes')}
              >
                <InputText
                  icon="clock"
                  value={test.duration}
                  onChange={({ target: { value } }) => {
                    setIsDirty({ ...isDirty, duration: true });
                    const maxLength = 3;
                    const newVal = parseInt(value.toString().substring(0, maxLength));
                    setTest({ ...test, duration: newVal });
                  }}
                  placeholder={t('No duration')}
                  type={'number'}
                  min="1"
                  max="480"
                />
              </FormField>
            </div>
          )}
          {!isPublisherMode && isPublishScoreVisible && (
            <PublishScoreSection
              t={t}
              rootClassName="new-test-form__big new-test-form__publish-score"
              classPrefix="new-test-form__publish-score"
              test={test}
              disabled={shouldDisablePublishScore}
              hasPublishScoreBeforeDueDate={hasPublishScoreBeforeDueDate}
              onToggleAutoPublishScore={onToggleAutoPublishScore}
              onChangePublishScoreAt={onChangePublishScoreAt}
              onClearPublishScoreAt={onClearPublishScoreAt}
              onToggleSameAsDueDate={onToggleSameAsDueDate}
            />
          )}
        </div>
        <div className="new-test-form__buttons">
          <FooterButtons
            disabledSuccess={!canSubmit}
            onSuccess={onSubmit}
            buttonTextCancel={t('actions:Cancel')}
            buttonTextSucess={isEditing ? t('actions:Save') : buttonTextSuccess}
            onCancel={onClickCancel}
          />
        </div>
      </form>
    </div>
  );
};

NewTestForm.propTypes = {
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  students: PropTypes.array,
  isPublisherMode: PropTypes.bool,
  t: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  currentStudents: PropTypes.array,
  currentTest: PropTypes.object,
  sections: PropTypes.array,
  onSectionCreated: PropTypes.func,
  preSelectionType: PropTypes.string,
  setSelectedTemplate: PropTypes.func,
  buttonTextSuccess: PropTypes.string,
  isAutoevaluative: PropTypes.bool,
  shouldShowPublishScore: PropTypes.bool,
};

NewTestForm.defaultProps = {
  isPublisherMode: false,
  onCancel: function () {},
  onSuccess: function () {},
  isAutoevaluative: true,
};

export default withTranslation(['lesson', 'common', 'placeholder', 'actions', 'assigments'])(NewTestForm);
